import { customElement, GenesisElement, observable, Tabs } from '@genesislcap/web-core';
import { Connect } from '@genesislcap/foundation-comms';
import { Modal } from '@genesislcap/foundation-ui';
import { App } from '@genesislcap/foundation-shell/app';
import { applyListingUpdatesToDetailView } from './deals.route.util';
import { DealStatusEnum, DetailViewDeal } from '../../../../pbi/components/deals/deals.types';
import { dealsTemplate as template, MAX_DETAIL_TABS } from './deals.template';
import { dealsStyles as styles } from './deals.styles';

@customElement({
  name: 'deals-route',
  template,
  styles
})
export class DealsRoute extends GenesisElement {
  tabs: Tabs;

  maxTabsModal: Modal;

  @observable dealToAdd: DetailViewDeal | null = null;

  @observable detailViewDeals: DetailViewDeal[] = [];

  @observable activeTabId: string = 'default';

  /** Per-deal reconfirm eligibility (from deal-detail). Used so tab only blinks when deal has orders eligible for reconfirm. */
  @observable reconfirmEligibilityMap: Map<string, boolean> = new Map();

  @App app: App;

  @Connect connect!: Connect;

  handleTabChange(): void {
    this.activeTabId = String(this.tabs?.activeid ?? 'default');
    window.dispatchEvent(new Event('resize')); // nudge grids after tab switch (listing layout after zoom on detail tab)
  }

  /**
   * CSS classes for a detail tab: reconfirm highlight only when the deal has at least one reconfirm-eligible order.
   * In Verbal Award/CXL Window with nothing to reconfirm → no tab styling (avoids false-positive highlight).
   * Active tab never blinks; inactive tabs with eligible orders blink.
   * Note: activeTabId and reconfirmEligibilityMap are passed as parameters to ensure FAST's reactivity
   * system tracks them as dependencies, triggering re-evaluation when they change.
   */
  getDetailTabClass(
    deal: DetailViewDeal,
    _activeTabId?: string,
    _reconfirmEligibilityMap?: Map<string, boolean>,
  ): string {
    if (deal.DEAL_STATUS !== DealStatusEnum.VERBAL_AWARD_CXL_WINDOW) {
      return '';
    }
    const dealIdStr = String(deal.DEAL_ID);
    if (!this.reconfirmEligibilityMap.get(dealIdStr)) {
      return '';
    }
    const classes: string[] = ['tab-reconfirm-window'];
    if (dealIdStr !== this.activeTabId) {
      classes.push('tab-reconfirm-window-blink');
    }
    if (dealIdStr === this.activeTabId) {
      classes.push('tab-reconfirm-window-active');
    }
    return classes.join(' ');
  }

  closeDetailView(e: CustomEvent, deal: DetailViewDeal): void {
    e.stopPropagation();
    e.preventDefault();
    this.removeDetailView(deal);
  }

  handleRowClick(e: CustomEvent): void {
    const row = e.detail.data;
    if (this.detailViewDeals.find(r => r.DEAL_ID === row.DEAL_ID)) {
      this.setActiveTab(row.DEAL_ID);
      return;
    }

    if (this.detailViewDeals.length < MAX_DETAIL_TABS) {
      this.addDetailView(row);
    } else {
      this.dealToAdd = row;
      this.maxTabsModal.show();
    }
  }

  addDetailView(deal: DetailViewDeal): void {
    this.detailViewDeals = [...this.detailViewDeals, deal];
    setTimeout(() => this.setActiveTab(deal.DEAL_ID), 100);
  }

  removeDetailViewBeforeAdding(deal: DetailViewDeal): void {
    this.removeDetailView(deal);
    this.dismissModalAndAdd();
  }

  closeAllDeals(): void {
    this.detailViewDeals = [];
    // Reset active tab back to listing before potentially opening a new deal tab
    this.activeTabId = 'default';
    if (this.tabs) {
      this.tabs.activeid = 'default';
    }
    this.dismissModalAndAdd();
  }

  cancelClose(): void {
    this.maxTabsModal.close();
  }

  handleDealUpdatedFromListing(e: CustomEvent<{ deals: DetailViewDeal[] }>): void {
    const deals = e.detail?.deals ?? [];
    if (!deals.length || !this.detailViewDeals.length) return;
    const { updated, changed } = applyListingUpdatesToDetailView(this.detailViewDeals, deals);
    if (changed) this.detailViewDeals = updated;
  }

  handleReconfirmEligibilityChanged(e: CustomEvent<{ dealId: string; hasReconfirmEligibleOrders: boolean }>): void {
    const { dealId, hasReconfirmEligibleOrders } = e.detail ?? {};
    if (dealId == null) return;
    this.reconfirmEligibilityMap.set(dealId, hasReconfirmEligibleOrders);
    this.reconfirmEligibilityMap = new Map(this.reconfirmEligibilityMap);
  }

  private setActiveTab(dealId: unknown): void {
    const id = String(dealId);
    this.activeTabId = id;
    // Keep rapid-tabs internal active id in sync to avoid cases where
    // the header changes but content stays on the listing tab.
    if (this.tabs) {
      this.tabs.activeid = id;
    }
  }

  private dismissModalAndAdd(): void {
    if (this.dealToAdd) {
      this.addDetailView(this.dealToAdd);
      this.dealToAdd = null;
    }
    this.maxTabsModal.close();
  }

  private removeDetailView(deal: DetailViewDeal): void {
    this.detailViewDeals = this.detailViewDeals.filter((d) => d !== deal);
    const dealId = deal.DEAL_ID;
    if (dealId == null) return;
    const id = String(dealId);
    this.reconfirmEligibilityMap.delete(id);
    this.reconfirmEligibilityMap = new Map(this.reconfirmEligibilityMap);
  }
}
